/**
 * Customer trust stack: one definition of the AWS console launch link and of
 * the setup steps, shared by the onboarding page (frontend) and the
 * "send instructions to your AWS administrator" email (backend), so the two
 * can never describe different steps (ENTRAID-260).
 *
 * What the launch link carries, and why none of it is a secret:
 *   - CustomerId: the customer's slug. It doubles as the sts:ExternalId, which
 *     AWS documents as a confused-deputy guard, not a secret: it only has to
 *     be unique per customer and chosen by onGuard, never by the customer.
 *   - PortalLambdaRoleArn: the onGuard role allowed to assume the trust role.
 *   - RegistrationTopicArn: the onGuard SNS topic the stack notifies.
 *   - RegistrationToken: a random single-use nonce, minted per link and per
 *     AWS administrator email, valid 7 days, stored hashed, and void once any
 *     registration succeeds (lib/trust-registration.mjs, ENTRAID-263). It
 *     only lets a stack *report* a trust role for this customer, once.
 *     onGuard checks the role itself (name, account), and the signed-in
 *     customer admin confirms the detected AWS account before anything is
 *     deployed, so a leaked token cannot move data or credentials anywhere.
 */

export const TRUST_STACK_REGION = 'us-east-1';

/**
 * Trust stack versions (customer isolation, 2026-10-26). Version 3 scopes the
 * trust role to the customer's own resources (its ClientName and CustomerId)
 * and adds the permissions boundary every collector role carries. A stack
 * that does not report a version (its role cannot read its own stack) is a
 * version 2 or older stack: its role still reaches every ipc-onguard-*
 * resource in the account. The portal keeps working with it; the customer
 * admin is asked to update. Bump together with the template's Metadata and
 * TrustStackVersion output.
 */
export const TRUST_STACK_VERSION = 3;
export const LEGACY_TRUST_STACK_VERSION = 2;
export const CLIENT_NAME_RE = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;
export const BOUNDARY_POLICY_PREFIX = 'IPCOnGuardBoundary-';

export const STACK_NAME_PREFIX = 'ipc-onguard-entra-';
// ClientName feeds every resource name in the collector stack; the longest one, the evidence bucket
// ipc-onguard-evidence-<ClientName>-<AccountId>, must stay within the 63 characters S3 allows (21 + 29 + 1 + 12).
export const MAX_CLIENT_NAME_LENGTH = 29;
// A stored name may be 30 characters: names were capped at 30 before ENTRAID-350. Such a legacy collector keeps its name
// (renaming would replace every resource), even though a 30 character name cannot have created its evidence bucket.
const STORED_CLIENT_NAME_RE = /^[a-z0-9][a-z0-9-]{0,29}$/;

function customerNameSlug(customer) {
  const raw = String(customer?.companyName || String(customer?.customerId ?? '').slice(0, 8)).toLowerCase();
  const slug = raw.replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '').slice(0, MAX_CLIENT_NAME_LENGTH).replace(/-+$/, '') || 'customer';
  // The collector and trust stacks need at least 3 characters (CLIENT_NAME_RE): "HP" becomes "hp-onguard".
  return CLIENT_NAME_RE.test(slug) ? slug : `${slug}-onguard`;
}

/**
 * ENTRAID-350: the collector's ClientName (and so its stack name and every resource name in the customer's AWS account).
 * Once a stack exists its name is kept, so a later company name change never renames, or duplicates, a deployed
 * collector. Before that, the name the trust stack was launched with (trustClientName, pinned at the first launch link)
 * wins, so a version 3 trust role, scoped to that name, always matches the collector it deploys. A new customer's name
 * comes from its company name, as before.
 */
export function collectorClientName(customer) {
  for (const candidate of [
    typeof customer?.stackName === 'string' && customer.stackName.startsWith(STACK_NAME_PREFIX) ? customer.stackName.slice(STACK_NAME_PREFIX.length) : null,
    customer?.trustClientName,
  ]) {
    if (typeof candidate === 'string' && STORED_CLIENT_NAME_RE.test(candidate)) return candidate;
  }
  return customerNameSlug(customer);
}

/** The boundary policy ARN a version 3 trust stack creates for the customer. */
export function boundaryPolicyArnFor(accountId, customerId) {
  return `arn:aws:iam::${accountId}:policy/${BOUNDARY_POLICY_PREFIX}${customerId}`;
}

/**
 * Is the customer's recorded trust stack older than the current version?
 * Only a customer with a registered trust role and a recorded check can be
 * outdated: an unchecked one is unknown (null), never a false alarm.
 */
export function trustStackOutdated(customer) {
  if (!customer?.trustRoleArn) return false;
  const v = customer.trustStackVersion;
  if (!Number.isInteger(v)) return null;
  return v < TRUST_STACK_VERSION;
}

/**
 * The trust stack's scope matches the collector it serves: false only when
 * a version 3 stack reports a ClientName other than the one the collector
 * stack uses (the role then cannot manage that collector).
 */
export function trustScopeMatches(customer, collectorClientName) {
  const scoped = customer?.trustStackClientName;
  if (!scoped || !collectorClientName) return true;
  return scoped === collectorClientName;
}
export const TRUST_ROLE_NAME_PREFIX = 'IPCOnGuardTrustRole-';
export const AWS_CONSOLE_SIGN_IN_URL = 'https://console.aws.amazon.com/';

export function trustStackName(customerId) {
  return `IPCOnGuardTrust-${customerId}`;
}

export function trustRoleArnFor(accountId, customerId) {
  return `arn:aws:iam::${accountId}:role/${TRUST_ROLE_NAME_PREFIX}${customerId}`;
}

/**
 * AWS CloudFormation quick-create link for the trust stack. The region is
 * fixed so the stack (and its SNS custom resource, which must live in the
 * same region as the topic) is always in us-east-1.
 */
export function buildTrustStackLaunchUrl({
  templatesBaseUrl,
  customerId,
  clientName,
  portalLambdaRoleArn,
  registrationTopicArn,
  registrationToken,
}) {
  const params = new URLSearchParams({
    templateURL: `${templatesBaseUrl}/customer-trust-stack.yaml`,
    stackName: trustStackName(customerId),
    param_CustomerId: customerId,
  });
  // Version 3: the role is scoped to this collector name. Always sent by the portal; a link without it leaves the
  // field empty in the console, which then refuses the stack until a valid name is typed (never a broad role).
  if (CLIENT_NAME_RE.test(String(clientName ?? ''))) params.set('param_ClientName', clientName);
  params.set('param_PortalLambdaRoleArn', portalLambdaRoleArn || 'PLACEHOLDER');
  // Both or neither: the template only creates the registration custom
  // resource when both are present, so an older link still works.
  if (registrationTopicArn && registrationToken) {
    params.set('param_RegistrationTopicArn', registrationTopicArn);
    params.set('param_RegistrationToken', registrationToken);
  }
  return `https://${TRUST_STACK_REGION}.console.aws.amazon.com/cloudformation/home?region=${TRUST_STACK_REGION}#/stacks/create/review?${params}`;
}

/**
 * AWS CloudFormation console link that opens the update of the customer's
 * existing trust stack with the current template (S4, self-service update).
 * It carries no registration token: an update never rebinds the customer to
 * another role. The console ignores param_ values on an update, so the portal
 * passes templateUrl: the customer's own copy of the template whose ClientName
 * Default is the collector name (backend lib/trust-update-template.mjs), and
 * the field is filled in. Only a copy under <templatesBaseUrl>/trust/ is
 * accepted; without one the link uses the canonical template (the name is
 * then typed, see trustStackUpdateSteps). param_ClientName stays as a hint.
 */
export function buildTrustStackUpdateUrl({ templatesBaseUrl, customerId, clientName, templateUrl }) {
  if (!CLIENT_NAME_RE.test(String(clientName ?? ''))) throw new Error('buildTrustStackUpdateUrl: a valid clientName is required');
  const copy = typeof templateUrl === 'string' && templateUrl.startsWith(`${templatesBaseUrl}/trust/`) ? templateUrl : null;
  const params = new URLSearchParams({
    stackId: trustStackName(customerId),
    templateURL: copy ?? `${templatesBaseUrl}/customer-trust-stack.yaml`,
    param_ClientName: clientName,
  });
  return `https://${TRUST_STACK_REGION}.console.aws.amazon.com/cloudformation/home?region=${TRUST_STACK_REGION}#/stacks/update/template?${params}`;
}

/**
 * The update steps, in order, for the self-service notice. prefilled (the
 * default, the normal case): the link's template already fills in ClientName.
 * prefilled false: the fallback when the customer's copy could not be
 * published, and the admin types the name once.
 */
export function trustStackUpdateSteps(clientName, { prefilled = true } = {}) {
  return [
    'Sign in to the AWS Management Console with the AWS account where onGuard is deployed.',
    'Open the update link. It opens the update of the existing IPCOnGuardTrust stack with the new template.',
    prefilled
      ? `Choose Next, and keep every parameter as it is. ClientName is already filled in with ${clientName}.`
      : `Choose Next, keep every parameter as it is, and type ${clientName} in ClientName (it is empty).`,
    `At the bottom of the page, check "I acknowledge that AWS CloudFormation might create IAM resources with custom names."`,
    'Choose Submit. The stack reaches UPDATE_COMPLETE in about a minute, and onGuard detects the new version.',
  ];
}

export const IAM_ACKNOWLEDGMENT = 'I acknowledge that AWS CloudFormation might create IAM resources with custom names.';

/** The setup steps, in order, as plain sentences (the page adds its own emphasis). */
export function trustStackSteps() {
  return [
    'Sign in to the AWS Management Console with the AWS account where onGuard will be deployed.',
    'Open the launch link. It opens AWS CloudFormation with every parameter already filled in.',
    `At the bottom of the page, check "${IAM_ACKNOWLEDGMENT}"`,
    'Choose Create stack. The stack reaches CREATE_COMPLETE in about a minute, and onGuard detects it automatically.',
  ];
}

/** Plain-text instructions for "Copy instructions" and the email text part. */
export function trustStackInstructionsText({ companyName, launchUrl }) {
  const who = companyName ? `${companyName} is` : 'Your organization is';
  const steps = trustStackSteps().map((s, i) => `${i + 1}. ${s}`).join('\n');
  return `${who} setting up onGuard for Entra ID and needs an IAM role in AWS.\n\n`
    + `You need permission to create CloudFormation stacks and IAM roles (for example AdministratorAccess).\n\n`
    + `${steps}\n\n`
    + `Sign-in: ${AWS_CONSOLE_SIGN_IN_URL}\n`
    + `Launch link: ${launchUrl}\n\n`
    + 'The stack creates one IAM role that lets IPC Global manage only this organization\'s onGuard collector in this account, '
    + 'and the permissions boundary that collector\'s roles carry. '
    + 'You can review the template before creating the stack.';
}
